// src/components/FleetHealthView.jsx
//
// Fleet health: one card per plant, showing whether its agent is reporting and
// how healthy the machine underneath it is.
//
// Two rules shape this component:
//
//  1. The status pill is NOT derived here. The backend decides
//     online/degraded/offline and this renders what it is given, so the pill can
//     never disagree with the alert email that was sent.
//
//  2. Every diagnostic cell must render cleanly when its value is null. The
//     agent-side diagnostics are a later phase; until they land, `diag` is null
//     for every plant, each cell shows a dash, and `degraded` never occurs. This
//     view lights up on its own when the data starts arriving.

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Activity, RefreshCw, Cpu, MemoryStick, HardDrive, Database, Timer, Plug, AlertCircle
} from 'lucide-react';
import {
  fetchFleetHealth, HEALTH_POLL_MS, DISK_WARN_PCT, DATA_STALE_SEC
} from '../services/fleetHealthService.js';
import { relativeTime, absoluteTime } from '../utils/relativeTime.js';

const STATUS = {
  online:   { label: 'Online',   color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  degraded: { label: 'Degraded', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  offline:  { label: 'Offline',  color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
};

const DASH = '—';

function StatusPill({ status }) {
  const s = STATUS[status] || { label: status || 'Unknown', color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' };
  return (
    <span
      data-testid={`status-pill-${status}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 999,
        background: s.bg, color: s.color,
        border: `1px solid ${s.color}55`,
        fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.color }} />
      {s.label}
    </span>
  );
}

/** One diagnostic reading. `value` of null renders a dash, never an error. */
function Cell({ icon: Icon, label, value, unit = '', warn = false, title }) {
  const known = value !== null && value !== undefined;
  return (
    <div
      title={title || label}
      data-testid={`diag-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 13,
        color: warn ? '#f59e0b' : (known ? '#e5e7eb' : '#6b7280'),
      }}
    >
      <Icon size={14} style={{ opacity: 0.7 }} />
      <span style={{ color: '#9ca3af' }}>{label}</span>
      <strong>{known ? `${value}${unit}` : DASH}</strong>
    </div>
  );
}

function PlantHealthRow({ plant, now }) {
  const diag = plant.diag || null;
  const sys = diag?.system || {};
  const net = diag?.network || {};
  const pipe = diag?.pipeline || {};

  const diskWarn = typeof sys.diskFreePct === 'number' && sys.diskFreePct < DISK_WARN_PCT;
  const staleWarn = typeof pipe.dataFreshnessSec === 'number' && pipe.dataFreshnessSec > DATA_STALE_SEC;
  const questdbDown = pipe.questdbReachable === false;

  const seenLabel = plant.lastSeen != null ? relativeTime(plant.lastSeen, now) : 'never';

  return (
    <div data-testid={`plant-row-${plant.plantId}`} style={styles.row}>
      <div style={styles.rowMain}>
        <div>
          <div style={styles.plantName}>{plant.name}</div>
          <div style={styles.plantId}>{plant.plantId}</div>
        </div>
        <StatusPill status={plant.status} />
      </div>

      <div style={styles.seen} title={absoluteTime(plant.lastSeen)}>
        {plant.status === 'offline'
          ? <span style={{ color: '#f87171' }}>Offline since {seenLabel}</span>
          : <span>Last seen {seenLabel}</span>}
      </div>

      <div style={styles.cells}>
        <Cell icon={Cpu} label="CPU" value={sys.cpuLoad1 ?? null} title="1-minute load average" />
        <Cell icon={MemoryStick} label="Mem" value={sys.memUsedPct ?? null} unit="%" title="Memory used" />
        <Cell
          icon={HardDrive} label="Disk free" value={sys.diskFreePct ?? null} unit="%"
          warn={diskWarn} title={`Free disk (warns below ${DISK_WARN_PCT}%)`}
        />
        <Cell
          icon={Database} label="QuestDB"
          value={pipe.questdbReachable === undefined || pipe.questdbReachable === null
            ? null
            : (pipe.questdbReachable ? 'up' : 'down')}
          warn={questdbDown} title="QuestDB reachable from the agent"
        />
        <Cell
          icon={Timer} label="Data age" value={pipe.dataFreshnessSec ?? null} unit="s"
          warn={staleWarn} title={`Age of the newest data (warns above ${DATA_STALE_SEC}s)`}
        />
        <Cell icon={Plug} label="Reconnects" value={net.reconnects ?? null} title="Agent reconnects" />
      </div>

      {!diag && (
        <div style={styles.awaiting}>Awaiting diagnostics from this agent.</div>
      )}
    </div>
  );
}

export default function FleetHealthView() {
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const rows = await fetchFleetHealth({ signal: controller.signal });
      setPlants(rows);
      setError(null);
    } catch (err) {
      if (err.name === 'AbortError') return;
      // Fail soft: the notice replaces the list, and the rest of the admin
      // dashboard is untouched. Keep whatever rows we already had.
      setError(err.message || 'Could not load fleet health');
    } finally {
      setNow(Date.now());
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();

    let timer = null;
    const start = () => {
      stop();
      timer = setInterval(load, HEALTH_POLL_MS);
    };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

    // Don't poll a tab nobody is looking at; refresh immediately on return so
    // the view is never stale when it becomes visible.
    const onVisibility = () => {
      if (document.hidden) stop();
      else { load(); start(); }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      abortRef.current?.abort();
    };
  }, [load]);

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <h2 style={styles.cardTitle}>
          <Activity size={20} />
          Fleet Health
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={styles.legend}>
            {['online', 'degraded', 'offline'].map((k) => (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS[k].color }} />
                <span style={{ color: '#9ca3af', fontSize: 12 }}>{STATUS[k].label}</span>
              </span>
            ))}
          </div>
          <button style={styles.iconBtn} onClick={load} title="Refresh now" aria-label="Refresh fleet health">
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="fleet-health-error" style={styles.notice}>
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {loading && plants.length === 0 ? (
        <div style={styles.muted}>Loading…</div>
      ) : plants.length === 0 ? (
        <div data-testid="fleet-health-empty" style={styles.muted}>
          {error ? 'Fleet health is unavailable right now.' : 'No plants yet — create one below.'}
        </div>
      ) : (
        <div style={styles.table}>
          {plants.map((p) => <PlantHealthRow key={p.plantId} plant={p} now={now} />)}
        </div>
      )}
    </div>
  );
}

// Matches the Plants-tab vocabulary in Admindashboard.jsx.
const styles = {
  card: {
    background: 'rgba(15,23,42,0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: 16,
    padding: 24,
    border: '1px solid #1e293b',
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20,
  },
  cardTitle: {
    fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10,
  },
  legend: { display: 'flex', gap: 12, alignItems: 'center' },
  iconBtn: {
    background: 'none', border: '1px solid #1e293b', borderRadius: 8,
    color: '#9ca3af', padding: 8, cursor: 'pointer',
  },
  table: { display: 'flex', flexDirection: 'column', gap: 12 },
  row: {
    padding: 16, background: '#020617', border: '1px solid #1e293b',
    borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 10,
  },
  rowMain: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  plantName: { fontSize: 16, fontWeight: 600 },
  plantId: { fontSize: 13, color: '#6b7280' },
  seen: { fontSize: 13, color: '#9ca3af' },
  cells: { display: 'flex', flexWrap: 'wrap', gap: 18 },
  awaiting: { fontSize: 12, color: '#6b7280', fontStyle: 'italic' },
  muted: { color: '#9ca3af', padding: '12px 0' },
  notice: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 12px', marginBottom: 14,
    background: 'rgba(245,158,11,0.10)', border: '1px solid #f59e0b55',
    borderRadius: 8, color: '#f59e0b', fontSize: 13,
  },
};
